// WHAT: Oura Sync Button Component
// WHY:  Button component for triggering Oura data synchronization
// DOMAIN: Health → Oura
// DEPENDENCIES: Global progress context, Tauri core API, Oura data context, debug logging
// RISKS:
//   - NOTE: Sync operation may take long time (no timeout)
//   - NOTE: Error handling is minimal (only logs, no user notification)
//   - NOTE: Reload happens after sync (may fail if sync partially succeeded)

import { core } from "@tauri-apps/api";
import { type FC, useState } from "react";

import { debugLog } from "@/domains/logging/utils/debugLog";
import { useGlobalProgress } from "@/shared/contexts";

import { useOuraData } from "../../contexts/OuraDataContext";

/**
 * WHAT:
 *   Icon button component for triggering Oura data synchronization.
 *
 * WHY:
 *   Provides quick access to sync functionality with visual feedback (loading spinner).
 *   Integrates with global progress indicator and Oura data context.
 *
 * BEHAVIOR:
 *   - Shows refresh icon (🔄) when idle, spinner when loading
 *   - Triggers backend sync_oura_data command via Tauri
 *   - Activates global progress indicator during sync
 *   - Reloads Oura data context after successful sync
 *   - Logs all operations using debug logging system
 *   - Button disabled during sync operation
 *
 * RISKS:
 *   - NOTE: Sync operation may take long time (no timeout mechanism)
 *   - NOTE: Errors are logged but not displayed to user
 *   - NOTE: Reload happens even if sync partially failed
 *   - NOTE: No retry mechanism for failed syncs.
 *
 * @returns JSX element displaying sync button.
 *
 */
export const OuraSyncButton: FC = () => {
  const { endSync, startSync } = useGlobalProgress();
  const { reload } = useOuraData();
  const [loading, setLoading] = useState(false);

  type FrontendDebugLog = (
    level: "debug" | "error" | "info" | "warn",
    domain: string,
    message: string,
    context?: Record<string, unknown>,
  ) => void;

  const safeDebugLog: FrontendDebugLog = debugLog as FrontendDebugLog;

  const handleClick = async () => {
    safeDebugLog("info", "health", "Oura sync button clicked", {
      file: "OuraSyncButton.tsx",
      function: "handleClick",
      module: "oura",
    });

    setLoading(true);
    startSync(); // Footer-Loader aktivieren
    try {
      safeDebugLog("info", "health", "Calling sync_oura_data command", {
        file: "OuraSyncButton.tsx",
        function: "handleClick",
        module: "oura",
      });
      await core.invoke("sync_oura_data");
      safeDebugLog("info", "health", "Oura sync command completed successfully", {
        file: "OuraSyncButton.tsx",
        function: "handleClick",
        module: "oura",
      });

      // Nach erfolgreichem Sync: UI-Daten aus Supabase neu laden
      safeDebugLog("info", "health", "Reloading UI data from Supabase after sync", {
        file: "OuraSyncButton.tsx",
        function: "handleClick",
        module: "oura",
      });
      await reload();
      safeDebugLog("info", "health", "UI data reloaded successfully", {
        file: "OuraSyncButton.tsx",
        function: "handleClick",
        module: "oura",
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      safeDebugLog("error", "health", `Oura sync command failed: ${errorMessage}`, {
        file: "OuraSyncButton.tsx",
        function: "handleClick",
        module: "oura",
      });
    } finally {
      setLoading(false);
      endSync(); // Footer-Loader deaktivieren
    }
  };

  return (
    <button
      className="flex items-center justify-center w-8 h-8 rounded-full bg-transparent hover:bg-neutral-700 text-neutral-400 hover:text-white transition-colors disabled:opacity-50"
      disabled={loading}
      onClick={() => {
        void handleClick();
      }}
      title="Oura synchronisieren"
    >
      {loading ? (
        <div className="w-4 h-4 border-2 border-neutral-400 border-t-transparent rounded-full animate-spin" />
      ) : (
        <span className="text-sm">🔄</span>
      )}
    </button>
  );
};
